import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  useTransition
} from "react";
import PropTypes from "prop-types";
import "./Dashboard.css";
import NewNoteModal from "./Modals/NewNoteModal";
import EditNoteModal from "./Modals/EditNoteModal";
import AttachedFileModal from "./Modals/AttachedfileModal.jsx";
import ViewNoteModal from "./Modals/ViewNoteModal";
import toast from "react-hot-toast";
import InlineImageViewer from "./Modals/InlineImageViewer";
import NotesTab from "./NotesTab/NotesTab.jsx";
import DashboardMenu from "./Dashbord/DashboardMenu.jsx";
import DashboardHeader from "./Dashbord/DashboardHeader.jsx";
import NewTaskModal from "./Modals/NewTaskModal.jsx";
import UrlNoteModal from "./Modals/UrlNoteModal";

const MemoizedDashboardMenu = React.memo(DashboardMenu);
const MemoizedDashboardHeader = React.memo(DashboardHeader);
const MemoizedNotesTab = React.memo(NotesTab);

const getInitialUrlParams = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const sitenoteId = urlParams.get("SitenoteId");
  const taskId = urlParams.get("TaskId");

  if (sitenoteId && !isNaN(parseInt(sitenoteId))) {
    return { type: "note", id: parseInt(sitenoteId) };
  }

  if (taskId && !isNaN(parseInt(taskId))) {
    return { type: "task", id: parseInt(taskId) };
  }

  return null;
};

const initialUrlParams = getInitialUrlParams();

if (initialUrlParams) {
  const newUrl = window.location.pathname;
  window.history.replaceState({}, "", newUrl);
}

const Dashboard = ({
  notes,
  userid,
  userRole,
  refreshNotes,
  addSiteNote,
  updateNote,
  projects,
  jobs,
  tasks,
  onUploadDocument,
  onDeleteDocument,
  onLogout,
  workspaces,
  defaultUserWorkspaceID,
  defaultUserWorkspaceName,
  onUpdateDefaultWorkspace,
  fetchProjectAndJobs,
  fetchNotes,
  workspaceRole,
}) => {
  const [searchTerm, setSearchTerm] = useState(() => {
    const saved = localStorage.getItem("dashboardSearchTerm");
    return saved || "";
  });

  const [activeSearchTerm, setActiveSearchTerm] = useState(() => {
    const saved = localStorage.getItem("dashboardSearchTerm");
    return saved || "";
  });

  const [searchResults, setSearchResults] = useState(() => {
    const saved = localStorage.getItem("dashboardSearchResults");
    return saved ? JSON.parse(saved) : [];
  });

  const [selectedFilters, setSelectedFilters] = useState(() => {
    try {
      const saved = localStorage.getItem("dashboardSelectedFilters");
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          date: Array.isArray(parsed.date)
            ? parsed.date.filter((val) => val)
            : [],
          workspace: Array.isArray(parsed.workspace)
            ? parsed.workspace.filter((val) => val)
            : [],
          project: Array.isArray(parsed.project)
            ? parsed.project.filter((val) => val)
            : [],
          job: Array.isArray(parsed.job) ? parsed.job.filter((val) => val) : [],
          userName: Array.isArray(parsed.userName)
            ? parsed.userName.filter((val) => val)
            : [],
          task: Array.isArray(parsed.task)
            ? parsed.task.filter((val) => val)
            : [],
        };
      }
    } catch (error) {
      console.error("Error parsing saved filters:", error);
    }
    return {
      date: [],
      workspace: [],
      project: [],
      job: [],
      userName: [],
      task: [],
    };
  });

  const [defaultWorkspaceRole, setDefaultWorkspaceRole] = useState(null);
  const [loadingWorkspaceRole, setLoadingWorkspaceRole] = useState(false);
  const [filteredNotesFromApi, setFilteredNotesFromApi] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showNewTaskModal, setShowNewTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [showNewModal, setShowNewModal] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [showAttachedFileModal, setShowAttachedFileModal] = useState(false);
  const [selectedFileNote, setSelectedFileNote] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewNote, setViewNote] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(null);
  const [filterSearchTerm, setFilterSearchTerm] = useState("");
  const [loadingFiltered, setLoadingFiltered] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [role, setRole] = useState(null);
  const [hasActiveSearchText, setHasActiveSearchText] = useState(false);
  const [isRoleLoading, setIsRoleLoading] = useState(false);
  const [userWorkspaces, setUserWorkspaces] = useState();
  const [userWorkspace, setUserWorkspace] = useState();
  const [focusedRow, setFocusedRow] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [searchColumn, setSearchColumn] = useState("");
  const [prefilledData, setPrefilledData] = useState(null);
  const [showRequestWorkspaceModal, setShowRequestWorkspaceModal] =
    useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [showPriorityDropdown, setShowPriorityDropdown] = useState(false);
  const [priorityDropdownPosition, setPriorityDropdownPosition] = useState({
    x: 0,
    y: 0,
  });
  const [selectedNoteForPriority, setSelectedNoteForPriority] = useState(null);
  const [viewMode, setViewMode] = useState(() => {
    const saved = localStorage.getItem("dashboardViewMode");
    return saved || "cards";
  });
  const [modalSource, setModalSource] = useState("dashboard");
  const [expandedStacks, setExpandedStacks] = useState({});
  const [expandedCardLimit, setExpandedCardLimit] = useState({});
  const [inlineImagesMap, setInlineImagesMap] = useState({});
  const [loadingImages, setLoadingImages] = useState({});
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageNote, setSelectedImageNote] = useState(null);
  const [stackedJobs, setStackedJobs] = useState([]);
  const [filteredStackedJobs, setFilteredStackedJobs] = useState([]);
  const [loadingStackedJobs, setLoadingStackedJobs] = useState(false);
  const newNoteModalRef = useRef();
  const filterRef = useRef();
  const notesTabRef = useRef();
  const fetchNotesTimeoutRef = useRef(null);
  const saveFiltersDebounceRef = useRef(null);
  const abortControllers = useRef({});
  const filterOptionsCache = useRef({});
  const CACHE_TTL = 60000; 
  const [isMobile, setIsMobile] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [notesByTask, setNotesByTask] = useState({});
  const [loadingNotesByTask, setLoadingNotesByTask] = useState({});
  const [tasksByJob, setTasksByJob] = useState({});
  const [loadingTasksByJob, setLoadingTasksByJob] = useState({});
  const [isClearingFilters, setIsClearingFilters] = useState(false);
  const [highlightedTaskId, setHighlightedTaskId] = useState(null);
  const [taskPagination, setTaskPagination] = useState({});
  const [isFilterPending, startFilterTransition] = useTransition();

  const [urlNoteId, setUrlNoteId] = useState(
    initialUrlParams?.type === "note" ? initialUrlParams.id : null,
  );
  const [urlTaskId, setUrlTaskId] = useState(
    initialUrlParams?.type === "task" ? initialUrlParams.id : null,
  );
  const [isUrlNoteModalOpen, setIsUrlNoteModalOpen] =
    useState(!!initialUrlParams);
  const [modalItemType, setModalItemType] = useState(
    initialUrlParams?.type || "note",
  );

  const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/api`;

  useEffect(() => {}, [isUrlNoteModalOpen, urlNoteId]);

  const [filterOptions, setFilterOptions] = useState({
    date: [],
    workspace: [],
    project: [],
    job: [],
    userName: [],
    task: [],
  });

  const [loadingFilterOptions, setLoadingFilterOptions] = useState({
    date: false,
    workspace: false,
    project: false,
    job: false,
    userName: false,
    task: false,
  });

  const [filterOptionsLoaded, setFilterOptionsLoaded] = useState({
    date: false,
    workspace: false,
    project: false,
    job: false,
    userName: false,
    task: false,
  });

  const clearHighlightedTask = useCallback(() => {
    setHighlightedTaskId(null);
  }, []);
  
  // ========== Stacked View Persistent State ==========
const getInitialStackedState = () => {
  try {
    const saved = localStorage.getItem('stackedViewState');
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        expandedProjects: parsed.expandedProjects || {},
        expandedTasks: parsed.expandedTasks || {},
        activeTabByJob: parsed.activeTabByJob || {},
        assigneeFilters: parsed.assigneeFilters || {},
      };
    }
  } catch (e) {
    console.error('Failed to load stacked view state', e);
  }
  return {
    expandedProjects: {},
    expandedTasks: {},
    activeTabByJob: {},
    assigneeFilters: {},
  };
};

const [stackedState, setStackedState] = useState(getInitialStackedState);

// Persist to localStorage whenever stackedState changes
useEffect(() => {
  try {
    localStorage.setItem('stackedViewState', JSON.stringify(stackedState));
  } catch (e) {
    console.error('Failed to save stacked view state', e);
  }
}, [stackedState]);

// Helper to update a specific slice of the state
const updateStackedState = useCallback((key, value) => {
  setStackedState(prev => ({ ...prev, [key]: value }));
}, []);

// Individual setters for clarity
const setExpandedProjects = useCallback((valueOrUpdater) => {
  setStackedState(prev => ({
    ...prev,
    expandedProjects: typeof valueOrUpdater === 'function' 
      ? valueOrUpdater(prev.expandedProjects) 
      : valueOrUpdater,
  }));
}, []);

const setExpandedTasks = useCallback((valueOrUpdater) => {
  setStackedState(prev => ({
    ...prev,
    expandedTasks: typeof valueOrUpdater === 'function'
      ? valueOrUpdater(prev.expandedTasks)
      : valueOrUpdater,
  }));
}, []);

const setActiveTabByJob = useCallback((valueOrUpdater) => {
  setStackedState(prev => ({
    ...prev,
    activeTabByJob: typeof valueOrUpdater === 'function'
      ? valueOrUpdater(prev.activeTabByJob)
      : valueOrUpdater,
  }));
}, []);

const setAssigneeFilters = useCallback((valueOrUpdater) => {
  setStackedState(prev => ({
    ...prev,
    assigneeFilters: typeof valueOrUpdater === 'function'
      ? valueOrUpdater(prev.assigneeFilters)
      : valueOrUpdater,
  }));
}, []);
  const hasActiveFilters = useMemo(
    () => Object.values(selectedFilters).some((arr) => arr?.length > 0),
    [selectedFilters],
  );

  const handleHighlightNote = useCallback((noteId) => {
    setMobileFiltersOpen(false);
    if (notesTabRef.current && notesTabRef.current.highlightNote) {
      notesTabRef.current.highlightNote(noteId);
    }
  }, []);

  const generateFilterCombinations = useCallback((filters) => {
    const combinations = [];

    const hasAnyFilters = Object.values(filters).some((arr) => arr.length > 0);
    if (!hasAnyFilters) {
      return [];
    }
    const filterArrays = {};

    Object.keys(filters).forEach((filterType) => {
      const values = filters[filterType];
      if (values.length > 0) {
        filterArrays[filterType] = values;
      } else {
        filterArrays[filterType] = [null];
      }
    });

    const cartesianProduct = (arrays) => {
      return arrays.reduce(
        (acc, curr) => {
          const result = [];
          acc.forEach((a) => {
            curr.forEach((c) => {
              result.push([...a, c]);
            });
          });
          return result;
        },
        [[]],
      );
    };

    const orderedKeys = [
      "date",
      "workspace",
      "project",
      "job",
      "task",
      "userName",
    ];
    const valueArrays = orderedKeys.map((key) => filterArrays[key]);

    const allCombinations = cartesianProduct(valueArrays);

    allCombinations.forEach((combo) => {
      const combination = {};
      orderedKeys.forEach((key, index) => {
        if (combo[index] !== null) {
          combination[key] = combo[index];
        }
      });

      if (Object.keys(combination).length > 0) {
        combinations.push(combination);
      }
    });

    return combinations;
  }, []);

  const filterCombinations = useMemo(
    () => generateFilterCombinations(selectedFilters),
    [selectedFilters, generateFilterCombinations],
  );

  const fetchSimpleOptions = useCallback(
    async (filterType, signal) => {
      let endpoint = "";
      let responseField = "jobs";

      switch (filterType) {
        case "date":
          endpoint = `${apiUrl}/Filters/GetFilteredSiteNoteDate/${userid}`;
          break;
        case "workspace":
          endpoint = `${apiUrl}/Filters/GetFilteredWorkspace/${userid}`;
          break;
        case "project":
          endpoint = `${apiUrl}/Filters/GetFilteredProject/${userid}`;
          break;
        case "job":
          endpoint = `${apiUrl}/Filters/GetFilteredJob/${userid}`;
          break;
        case "task":
          endpoint = `${apiUrl}/Filters/GetFilteredTask/${userid}`;
          responseField = "tasks";
          break;
        case "userName":
          endpoint = `${apiUrl}/Filters/GetFilteredSiteNoteUser/${userid}`;
          break;
        default:
          return [];
      }

      const response = await fetch(endpoint, { signal });
      if (!response.ok)
        throw new Error(
          `Failed to fetch ${filterType} options: ${response.status}`,
        );
      const data = await response.json();
      return data[responseField] || [];
    },
    [apiUrl, userid],
  );

  const fetchFilteredOptions = useCallback(
    async (filterType, combination, signal) => {
      let endpoint = "";
      let responseField = "jobs";

      switch (filterType) {
        case "date":
          endpoint = `${apiUrl}/Filters/GetFilteredSiteNoteDate/${userid}`;
          break;
        case "workspace":
          endpoint = `${apiUrl}/Filters/GetFilteredWorkspace/${userid}`;
          break;
        case "project":
          endpoint = `${apiUrl}/Filters/GetFilteredProject/${userid}`;
          break;
        case "job":
          endpoint = `${apiUrl}/Filters/GetFilteredJob/${userid}`;
          break;
        case "userName":
          endpoint = `${apiUrl}/Filters/GetFilteredSiteNoteUser/${userid}`;
          break;
        case "task":
          endpoint = `${apiUrl}/Filters/GetFilteredTask/${userid}`;
          responseField = "tasks";
          break;
        default:
          return [];
      }

      const queryParams = new URLSearchParams();

      if (combination.workspace)
        queryParams.append("WorkspaceId", combination.workspace);
      if (combination.project)
        queryParams.append("ProjectId", combination.project);
      if (combination.job) queryParams.append("JobId", combination.job);
      if (combination.date) queryParams.append("NoteDate", combination.date);
      if (combination.task) queryParams.append("TaskId", combination.task);
      if (combination.userName)
        queryParams.append("UserId", combination.userName);

      const fullUrl = queryParams.toString()
        ? `${endpoint}?${queryParams.toString()}`
        : endpoint;

      const response = await fetch(fullUrl, { signal });
      if (!response.ok) {
        console.error(
          `Failed to fetch ${filterType} options:`,
          response.status,
        );
        return [];
      }

      const data = await response.json();
      return data[responseField] || [];
    },
    [apiUrl, userid],
  );

  const removeDuplicates = useCallback((items, filterType) => {
    const seen = new Set();
    const uniqueItems = [];

    for (const item of items) {
      let id;

      switch (filterType) {
        case "date":
          const dateStr = item.siteNoteDate;
          try {
            const date = new Date(dateStr);
            if (!isNaN(date.getTime())) {
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, "0");
              const day = String(date.getDate()).padStart(2, "0");
              id = `${year}-${month}-${day}`;
            } else {
              id = dateStr.split("T")[0];
            }
          } catch (e) {
            id = dateStr.split("T")[0];
          }
          break;
        case "workspace":
          id = item.workspaceId;
          break;
        case "project":
          id = item.projectId;
          break;
        case "job":
          id = item.jobId;
          break;
        case "userName":
          id = item.siteNoteUserName || item.siteNoteUserId;
          break;
        case "task":
          id = item.taskId;
          break;
        default:
          id = JSON.stringify(item);
      }

      if (!seen.has(id)) {
        seen.add(id);
        uniqueItems.push(item);
      }
    }

    return uniqueItems;
  }, []);

  useEffect(() => {
    return () => {
      if (saveFiltersDebounceRef.current) {
        clearTimeout(saveFiltersDebounceRef.current);
      }
    };
  }, []);
  const transformOptions = useCallback((filterType, items) => {
    switch (filterType) {
      case "date":
        return items.map((item) => {
          const dateStr = item.siteNoteDate;
          try {
            const date = new Date(dateStr);
            if (!isNaN(date.getTime())) {
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, "0");
              const day = String(date.getDate()).padStart(2, "0");
              const value = `${year}-${month}-${day}`;
              return {
                id: value,
                text: date.toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                }),
                displayText: date.toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                }),
                rawValue: dateStr,
              };
            }
          } catch (e) {
            console.error("Error parsing date:", e);
          }
          return {
            id: dateStr.split("T")[0],
            text: dateStr.split("T")[0],
            displayText: dateStr.split("T")[0],
            rawValue: dateStr,
          };
        });

      case "workspace":
        return items.map((item) => ({
          id: item.workspaceId,
          text: item.workspaceName,
          displayText: item.workspaceName,
          rawValue: item,
        }));

      case "project":
        return items.map((item) => ({
          id: item.projectId,
          text: item.projectName,
          displayText: item.projectName,
          rawValue: item,
        }));

      case "job":
        return items.map((item) => ({
          id: item.jobId,
          text: item.jobName,
          displayText: item.jobName,
          rawValue: item,
        }));

      case "userName":
        return items.map((item) => ({
          id: item.siteNoteUserId,
          text: item.siteNoteUserName,
          displayText: item.siteNoteUserName,
          rawValue: item,
        }));

      case "task":
        return items.map((item) => ({
          id: item.taskId,
          text: item.taskTitle,
          displayText: item.taskTitle,
          rawValue: item,
        }));

      default:
        return [];
    }
  }, []);

  useEffect(() => {
    setHasActiveSearchText(searchTerm.trim() !== "");
  }, [searchTerm]);
  
  useEffect(() => {
  const preloadAllFilters = async () => {
    const filterTypes = ["workspace", "project", "job", "userName", "task"];
    for (const type of filterTypes) {
      if (!filterOptionsLoaded[type] && !loadingFilterOptions[type]) {
        await fetchFilterOptions(type);
      }
    }
  };
  
  if (userid) {
    preloadAllFilters();
  }
}, [userid]); 

  useEffect(() => {
  // Clear cache when selected filters change
  filterOptionsCache.current = {};
}, [selectedFilters]);

const fetchFilterOptions = useCallback(
  async (filterType, signal) => {
    if (loadingFilterOptions[filterType] || !userid) return;

    // Build cache key including current selected filters (except the one we're fetching)
    const cacheKey = `${filterType}_${JSON.stringify(selectedFilters)}`;
    const cached = filterOptionsCache.current[cacheKey];
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      setFilterOptions((prev) => ({ ...prev, [filterType]: cached.data }));
      setFilterOptionsLoaded((prev) => ({ ...prev, [filterType]: true }));
      return cached.data;
    }

    setLoadingFilterOptions((prev) => ({ ...prev, [filterType]: true }));

    try {
      const otherFilters = {
        workspace: filterType !== "workspace" ? selectedFilters.workspace : [],
        project: filterType !== "project" ? selectedFilters.project : [],
        job: filterType !== "job" ? selectedFilters.job : [],
        date: filterType !== "date" ? selectedFilters.date : [],
        userName: filterType !== "userName" ? selectedFilters.userName : [],
        task: filterType !== "task" ? selectedFilters.task : [],
      };

      const hasFilters = Object.values(otherFilters).some((arr) => arr.length > 0);
      let allItems = [];

      if (!hasFilters) {
        const items = await fetchSimpleOptions(filterType, signal);
        allItems = items;
      } else {
        const combinations = generateFilterCombinations(otherFilters);
        const promises = combinations.map((combination) =>
          fetchFilteredOptions(filterType, combination, signal)
        );
        const results = await Promise.all(promises);
        results.forEach((items) => {
          allItems.push(...items);
        });
        allItems = removeDuplicates(allItems, filterType);
      }

      if (signal && signal.aborted) return;

      let transformedOptions = transformOptions(filterType, allItems);

      const uniqueOptions = Array.from(
        new Map(transformedOptions.map((item) => [item.id, item])).values()
      ).sort((a, b) => {
        if (typeof a.text === "string" && typeof b.text === "string") {
          return a.text.localeCompare(b.text);
        }
        return 0;
      });

      // Store in cache
      filterOptionsCache.current[cacheKey] = {
        data: uniqueOptions,
        timestamp: Date.now(),
      };

      setFilterOptions((prev) => ({
        ...prev,
        [filterType]: uniqueOptions,
      }));

      setFilterOptionsLoaded((prev) => ({
        ...prev,
        [filterType]: true,
      }));

      return uniqueOptions;
    } catch (error) {
      if (error.name === "AbortError") {
        console.log(`Fetch aborted for ${filterType}`);
        return;
      }
      console.error(`Error fetching ${filterType} options:`, error);
      toast.error(`Failed to load ${filterType} options`);
      return [];
    } finally {
      if (!signal || !signal.aborted) {
        setLoadingFilterOptions((prev) => ({ ...prev, [filterType]: false }));
      }
    }
  },
  [
    apiUrl,
    userid,
    loadingFilterOptions,
    selectedFilters,
    fetchSimpleOptions,
    fetchFilteredOptions,
    generateFilterCombinations,
    removeDuplicates,
    transformOptions,
  ]
);
  const reloadAllFilters = useCallback(async () => {
    const filterTypes = [
      "date",
      "workspace",
      "project",
      "job",
      "task",
      "userName",
    ];
    const promises = filterTypes.map((filterType) =>
      fetchFilterOptions(filterType),
    );
    try {
      await Promise.all(promises);
    } catch (error) {
      console.error("Error reloading filters:", error);
    }
  }, [fetchFilterOptions]);

  const groupFilteredNotesByJob = useCallback((notes) => {
    if (!notes || notes.length === 0) {
      return [];
    }

    const groupedByJob = {};

    notes.forEach((note) => {
      const jobName = note.job || "Unassigned";
      const jobId = note.jobId || note.job;

      if (!groupedByJob[jobName]) {
        groupedByJob[jobName] = {
          jobId: jobId,
          jobName: jobName,
          projectId: note.projectId || note.project || "",
          projectName: note.projectName || note.project || "",
          jobDescription: note.jobDescription || "",
          noteCount: 1,
          notes: [note],
          hasLoadedNotes: true,
          isLoadingNotes: false,
          errorLoadingNotes: null,
          latestTimeStamp: note.timeStamp || note.date,
          lastSiteNoteUserName: note.userName || note.UserName,
          lastSiteNote: note.note || "",
        };
      } else {
        groupedByJob[jobName].notes.push(note);
        groupedByJob[jobName].noteCount++;

        const noteTime = new Date(note.timeStamp || note.date).getTime();
        const currentLatest = new Date(
          groupedByJob[jobName].latestTimeStamp || 0,
        ).getTime();
        if (noteTime > currentLatest) {
          groupedByJob[jobName].latestTimeStamp = note.timeStamp || note.date;
          groupedByJob[jobName].lastSiteNote = note.note || "";
          groupedByJob[jobName].lastSiteNoteUserName =
            note.userName || note.UserName;
        }
      }
    });

   return Object.values(groupedByJob).sort((a, b) => {
  const dateA = new Date(a.latestTimeStamp || 0);
  const dateB = new Date(b.latestTimeStamp || 0);
  return dateB - dateA;
});
  }, []);

  const fetchNotesForTask = useCallback(
    async (taskId, jobId, page = 1, append = false) => {
      if (!taskId || !userid) return;
      if (loadingNotesByTask[taskId] && !append) return;

      setLoadingNotesByTask((prev) => ({ ...prev, [taskId]: true }));

      try {
        const pageSize = 20;
        const params = new URLSearchParams({
          pageNumber: page.toString(),
          pageSize: pageSize.toString(),
          taskId: taskId,
          userId: userid,
        });

        const url = `${apiUrl}/SiteNote/GetSiteNotesWithFilters?${params.toString()}`;
        const response = await fetch(url);

        if (!response.ok)
          throw new Error(`Failed to fetch task notes: ${response.status}`);

        const data = await response.json();
        const notesArray = data.data || data.siteNotes || [];
        const totalCount =
          data.totalCount ||
          data.total ||
          data.totalRecords ||
          notesArray.length;

        const currentOffset = (page - 1) * pageSize;
        const hasMore = currentOffset + notesArray.length < totalCount;

        const formattedNotes = notesArray.map((n) => ({
          ...n,
          id: n.id,
          userName: n.userName || n.UserName || "Unknown",
          documentCount: n.documentCount || 0,
          timeStamp: n.timeStamp || n.date,
          date: n.date || n.createdDate,
          workspace: n.workspace || "",
          project: n.project || "",
          job: n.job || "",
          note: n.note || n.content,
          jobId: n.jobId || jobId,
          taskId: n.taskId,
        }));

        setTaskPagination((prev) => {
          const existing = prev[taskId] || {
            page: 0,
            hasMore: true,
            total: totalCount,
            loadedCount: 0,
          };
          const newLoadedCount = append
            ? existing.loadedCount + formattedNotes.length
            : formattedNotes.length;
          const finalLoadedCount = Math.min(newLoadedCount, totalCount);
          const finalHasMore = finalLoadedCount < totalCount;

          return {
            ...prev,
            [taskId]: {
              page: page,
              hasMore: finalHasMore,
              total: totalCount,
              loadedCount: finalLoadedCount,
              pageSize: pageSize,
            },
          };
        });

        setNotesByTask((prev) => {
          const existingNotes = prev[taskId] || [];
          const updatedNotes = append
            ? [...existingNotes, ...formattedNotes]
            : formattedNotes;
          return { ...prev, [taskId]: updatedNotes };
        });

        return formattedNotes;
      } catch (error) {
        console.error("Error fetching task notes:", error);
        toast.error("Failed to load task notes");
        setTaskPagination((prev) => ({
          ...prev,
          [taskId]: {
            ...prev[taskId],
            hasMore: false,
            error: error.message,
          },
        }));
      } finally {
        setLoadingNotesByTask((prev) => ({ ...prev, [taskId]: false }));
      }
    },
    [apiUrl, userid, loadingNotesByTask],
  );

  const loadMoreTaskNotes = useCallback(
    async (taskId, jobId) => {
      const pagination = taskPagination[taskId];
      if (!pagination || !pagination.hasMore) return false;
      if (loadingNotesByTask[taskId]) return false;
      if (pagination.loadedCount >= pagination.total) {
        setTaskPagination((prev) => ({
          ...prev,
          [taskId]: { ...prev[taskId], hasMore: false },
        }));
        return false;
      }
      const nextPage = pagination.page + 1;
      await fetchNotesForTask(taskId, jobId, nextPage, true);
      return true;
    },
    [taskPagination, loadingNotesByTask, fetchNotesForTask],
  );

  const fetchTasksForJob = useCallback(
    async (jobId) => {
      if (!jobId || loadingTasksByJob[jobId]) return;
      setLoadingTasksByJob((prev) => ({ ...prev, [jobId]: true }));
      try {
        const response = await fetch(`${apiUrl}/JobTasks/job/${jobId}`);
        const result = await response.json();
        if (result.success && result.data) {
          setTasksByJob((prev) => ({ ...prev, [jobId]: result.data }));
          return result.data;
        }
      } catch (error) {
        console.error("Error fetching tasks:", error);
        toast.error("Failed to load tasks");
      } finally {
        setLoadingTasksByJob((prev) => ({ ...prev, [jobId]: false }));
      }
    },
    [apiUrl, loadingTasksByJob],
  );

  const fetchFilteredNotesForCombination = useCallback(
    async (combination, searchText) => {
      if (!userid) return [];

      try {
        const params = new URLSearchParams({
          pageNumber: "1",
          pageSize: "100",
          userId: userid,
        });

        if (combination.date) params.append("date", combination.date);
        if (combination.workspace)
          params.append("workspaceId", combination.workspace);
        if (combination.project)
          params.append("projectId", combination.project);
        if (combination.job) params.append("jobId", combination.job);
        if (combination.userName)
          params.append("siteNoteUserId", combination.userName);
        if (combination.task) params.append("taskId", combination.task);

        let url;
        if (searchText && searchText.trim() !== "") {
          params.append("searchText", searchText);
          url = `${apiUrl}/SiteNote/GetSiteNotesWithTextFilter?${params.toString()}`;
        } else {
          url = `${apiUrl}/SiteNote/GetSiteNotesWithFilters?${params.toString()}`;
        }

        const response = await fetch(url);
        if (!response.ok) return [];

        const data = await response.json();
        const notesList = data.data || data.siteNotes || [];
        return notesList.map((n) => ({
          ...n,
          id: n.id,
          userName: n.userName || n.UserName,
          documentCount: n.documentCount ?? n.DocumentCount ?? 0,
          note: n.note || n.content || "",
          timeStamp: n.timeStamp || n.date,
          date: n.date || n.createdDate || new Date().toISOString(),
          workspace: n.workspace || n.workspaceName || "",
          project: n.project || n.projectName || "",
          job: n.job || "",
          jobId: n.jobId || "",
        }));
      } catch (error) {
        console.error("Error fetching notes for combination:", error);
        return [];
      }
    },
    [apiUrl, userid],
  );

  const fetchNotesWithFilters = useCallback(
    async (filters) => {
      if (!userid) return;
      setLoadingFiltered(true);
      setFilteredStackedJobs([]);

      try {
        const filterCombinations = generateFilterCombinations(filters);
        if (filterCombinations.length === 0) {
          setFilteredNotesFromApi([]);
          setFilteredStackedJobs([]);
          return;
        }

        const allPromises = filterCombinations.map((combination) =>
          fetchFilteredNotesForCombination(combination, activeSearchTerm),
        );
        const allResults = await Promise.all(allPromises);

        const allNotes = [];
        const seenNoteIds = new Set();
        allResults.forEach((notes) => {
          notes.forEach((note) => {
            if (!seenNoteIds.has(note.id)) {
              seenNoteIds.add(note.id);
              allNotes.push(note);
            }
          });
        });

        setFilteredNotesFromApi(allNotes);
        if (allNotes.length > 0) {
          const groupedJobs = groupFilteredNotesByJob(allNotes);
          setFilteredStackedJobs(groupedJobs);
        } else {
          setFilteredStackedJobs([]);
        }
      } catch (error) {
        console.error("Error fetching filtered notes:", error);
        toast.error("Failed to fetch filtered notes");
        setFilteredNotesFromApi([]);
        setFilteredStackedJobs([]);
      } finally {
        setLoadingFiltered(false);
      }
    },
    [
      apiUrl,
      userid,
      groupFilteredNotesByJob,
      generateFilterCombinations,
      fetchFilteredNotesForCombination,
      activeSearchTerm,
    ],
  );

  const debouncedFetchNotesWithFilters = useCallback(
    (filters) => {
      if (fetchNotesTimeoutRef.current) {
        clearTimeout(fetchNotesTimeoutRef.current);
      }
      fetchNotesTimeoutRef.current = setTimeout(() => {
        fetchNotesWithFilters(filters);
      }, 300);
    },
    [fetchNotesWithFilters],
  );

  const fetchNotesForJob = useCallback(
    async (jobId, filters = null) => {
      if (!userid || !jobId) return [];

      try {
        if (
          viewMode === "stacked" &&
          activeSearchTerm &&
          activeSearchTerm.trim() !== ""
        ) {
          const activeFilters =
            filters && Object.values(filters).some((arr) => arr?.length > 0);
          if (activeFilters) {
            const combinations = generateFilterCombinations(filters);
            const promises = combinations.map((combo) => {
              const params = new URLSearchParams({
                pageNumber: "1",
                pageSize: "100",
                jobId: jobId,
                userId: userid,
                searchText: activeSearchTerm,
              });
              if (combo.date) params.append("date", combo.date);
              if (combo.workspace)
                params.append("workspaceId", combo.workspace);
              if (combo.project) params.append("projectId", combo.project);
              if (combo.userName) params.append("username", combo.userName);
              if (combo.task) params.append("taskId", combo.task);
              return fetch(
                `${apiUrl}/SiteNote/GetSiteNotesByJobIdWithTextFilter?${params.toString()}`,
              ).then((res) => (res.ok ? res.json() : { siteNotes: [] }));
            });

            const results = await Promise.all(promises);
            const seenIds = new Set();
            const combined = [];
            results.forEach((r) => {
              (r.siteNotes || []).forEach((n) => {
                if (!seenIds.has(n.id)) {
                  seenIds.add(n.id);
                  combined.push(n);
                }
              });
            });

            return combined.map((note) => ({
              ...note,
              id: note.id,
              userName: note.userName || note.UserName || "Unknown User",
              documentCount: note.documentCount || note.DocumentCount || 0,
              timeStamp: note.timeStamp || note.date || note.createdDate,
              date: note.date || note.createdDate || new Date().toISOString(),
              workspace: note.workspace || note.workspaceName || "",
              project: note.project || note.projectName || "",
              job: note.job || "",
              note: note.note || note.content || "",
              jobId: note.jobId || jobId,
            }));
          }

          const params = new URLSearchParams({
            pageNumber: "1",
            pageSize: "100",
            jobId: jobId,
            userId: userid,
            searchText: activeSearchTerm,
          });

          const notesResponse = await fetch(
            `${apiUrl}/SiteNote/GetSiteNotesByJobIdWithTextFilter?${params.toString()}`,
          );
          if (!notesResponse.ok)
            throw new Error(`Failed to fetch notes: ${notesResponse.status}`);
          const notesData = await notesResponse.json();
          return (notesData.siteNotes || []).map((note) => ({
            ...note,
            id: note.id,
            userName: note.userName || note.UserName || "Unknown User",
            documentCount: note.documentCount || note.DocumentCount || 0,
            timeStamp: note.timeStamp || note.date || note.createdDate,
            date: note.date || note.createdDate || new Date().toISOString(),
            workspace: note.workspace || note.workspaceName || "",
            project: note.project || note.projectName || "",
            job: note.job || "",
            note: note.note || note.content || "",
            jobId: note.jobId || jobId,
          }));
        }

        const params = new URLSearchParams({
          jobId: jobId,
          userId: userid,
          pageNumber: "1",
          pageSize: "100",
        });

        if (filters && hasActiveFilters) {
          const filterCombinations = generateFilterCombinations(filters);
          if (filterCombinations.length > 1) {
            const allPromises = filterCombinations.map(async (combination) => {
              const comboParams = new URLSearchParams({
                jobId: jobId,
                userId: userid,
                pageNumber: "1",
                pageSize: "100",
              });
              if (combination.date)
                comboParams.append("date", combination.date);
              if (combination.workspace)
                comboParams.append("workspaceId", combination.workspace);
              if (combination.project)
                comboParams.append("projectId", combination.project);
              if (combination.userName)
                comboParams.append("username", combination.userName);
              if (combination.task)
                comboParams.append("taskId", combination.task);
              if (activeSearchTerm && activeSearchTerm.trim() !== "") {
                comboParams.append("searchText", activeSearchTerm);
                const res = await fetch(
                  `${apiUrl}/SiteNote/GetSiteNotesByJobIdWithTextFilter?${comboParams.toString()}`,
                );
                return res.ok ? res.json() : { siteNotes: [] };
              } else {
                const res = await fetch(
                  `${apiUrl}/SiteNote/GetSiteNotesByJobId?${comboParams.toString()}`,
                );
                return res.ok ? res.json() : { siteNotes: [] };
              }
            });
            const allResults = await Promise.all(allPromises);
            const allNotes = [];
            const seenNoteIds = new Set();
            allResults.forEach((data) => {
              (data.siteNotes || []).forEach((note) => {
                if (!seenNoteIds.has(note.id)) {
                  seenNoteIds.add(note.id);
                  allNotes.push(note);
                }
              });
            });
            return allNotes.map((note) => ({
              ...note,
              id: note.id,
              userName: note.userName || note.UserName || "Unknown User",
              documentCount: note.documentCount || note.DocumentCount || 0,
              timeStamp: note.timeStamp || note.date || note.createdDate,
              date: note.date || note.createdDate || new Date().toISOString(),
              workspace: note.workspace || note.workspaceName || "",
              project: note.project || note.projectName || "",
              job: note.job || "",
              note: note.note || note.content || "",
              jobId: note.jobId || jobId,
            }));
          } else if (filterCombinations.length === 1) {
            const combination = filterCombinations[0];
            if (combination.date) params.append("date", combination.date);
            if (combination.workspace)
              params.append("workspaceId", combination.workspace);
            if (combination.project)
              params.append("projectId", combination.project);
            if (combination.userName)
              params.append("username", combination.userName);
            if (combination.task) params.append("taskId", combination.task);
          }
        }

        let notesResponse;
        if (activeSearchTerm && activeSearchTerm.trim() !== "") {
          params.append("searchText", activeSearchTerm);
          notesResponse = await fetch(
            `${apiUrl}/SiteNote/GetSiteNotesByJobIdWithTextFilter?${params.toString()}`,
          );
        } else {
          notesResponse = await fetch(
            `${apiUrl}/SiteNote/GetSiteNotesByJobId?${params.toString()}`,
          );
        }
        if (!notesResponse.ok)
          throw new Error(`Failed to fetch notes: ${notesResponse.status}`);
        const notesData = await notesResponse.json();
        return (notesData.siteNotes || []).map((note) => ({
          ...note,
          id: note.id,
          userName: note.userName || note.UserName || "Unknown User",
          documentCount: note.documentCount || note.DocumentCount || 0,
          timeStamp: note.timeStamp || note.date || note.createdDate,
          date: note.date || note.createdDate || new Date().toISOString(),
          workspace: note.workspace || note.workspaceName || "",
          project: note.project || note.projectName || "",
          job: note.job || "",
          note: note.note || note.content || "",
          jobId: note.jobId || jobId,
        }));
      } catch (error) {
        console.error(`Error fetching notes for job ${jobId}:`, error);
        throw error;
      }
    },
    [
      apiUrl,
      userid,
      hasActiveFilters,
      generateFilterCombinations,
      viewMode,
      activeSearchTerm,
    ],
  );

  const toggleStackExpansion = useCallback(
    async (jobName, jobId) => {
      const isExpanding = !expandedStacks[jobName];
      if (!isExpanding) {
        setExpandedStacks((prev) => ({ ...prev, [jobName]: false }));
        return;
      }

      const jobsSource = hasActiveFilters ? filteredStackedJobs : stackedJobs;
      const job = jobsSource.find((j) => j.jobId === jobId);
      if (!job) return;
      if (job.hasLoadedNotes && job.notes) {
        setExpandedStacks((prev) => ({ ...prev, [jobName]: true }));
        return;
      }

      if (hasActiveFilters) {
        setFilteredStackedJobs((prev) => {
          const updated = [...prev];
          const idx = updated.findIndex((j) => j.jobId === jobId);
          if (idx !== -1)
            updated[idx] = { ...updated[idx], isLoadingNotes: true };
          return updated;
        });
      } else {
        setStackedJobs((prev) => {
          const updated = [...prev];
          const idx = updated.findIndex((j) => j.jobId === jobId);
          if (idx !== -1)
            updated[idx] = { ...updated[idx], isLoadingNotes: true };
          return updated;
        });
      }

      try {
        await fetchTasksForJob(jobId);
        const updatedJob = {
          ...job,
          isLoadingNotes: false,
          hasLoadedNotes: true,
        };
        if (hasActiveFilters) {
          setFilteredStackedJobs((prev) => {
            const updated = [...prev];
            const idx = updated.findIndex((j) => j.jobId === jobId);
            if (idx !== -1) updated[idx] = updatedJob;
            return updated;
          });
        } else {
          setStackedJobs((prev) => {
            const updated = [...prev];
            const idx = updated.findIndex((j) => j.jobId === jobId);
            if (idx !== -1) updated[idx] = updatedJob;
            return updated;
          });
        }
        setExpandedStacks((prev) => ({ ...prev, [jobName]: true }));
      } catch (error) {
        console.error("Error expanding stack:", error);
        toast.error(`Failed to load data for ${jobName}`);
      }
    },
    [
      expandedStacks,
      stackedJobs,
      filteredStackedJobs,
      hasActiveFilters,
      fetchTasksForJob,
    ],
  );

  const fetchStackedJobs = useCallback(async () => {
    if (!userid) return;
    setLoadingStackedJobs(true);
    try {
      if (
        viewMode === "stacked" &&
        activeSearchTerm &&
        activeSearchTerm.trim() !== ""
      ) {
        if (hasActiveFilters) {
          const combinations = filterCombinations;
          const promises = combinations.map((combo) => {
            const params = new URLSearchParams({
              searchText: activeSearchTerm,
              userId: userid,
            });
            if (combo.date) params.append("date", combo.date);
            if (combo.workspace) params.append("workspaceId", combo.workspace);
            if (combo.project) params.append("projectId", combo.project);
            if (combo.job) params.append("jobId", combo.job);
            if (combo.userName) params.append("username", combo.userName);
            if (combo.task) params.append("taskId", combo.task);
            return fetch(
              `${apiUrl}/SiteNote/GetJobsBySiteNoteText?${params.toString()}`,
            ).then((res) => (res.ok ? res.json() : { jobs: [] }));
          });
          const results = await Promise.all(promises);
          const jobsMap = new Map();
          results.forEach((r) => {
            (r.jobs || []).forEach((job) => {
              if (!jobsMap.has(job.jobId)) jobsMap.set(job.jobId, job);
            });
          });
          const jobs = Array.from(jobsMap.values()).map((job) => ({
            jobId: job.jobId,
            jobName: job.jobName,
            jobDescription: job.jobDescription || "",
            noteCount: job.matchingNotesCount || job.totalSiteNotes || 0,
            latestTimeStamp: job.latestMatchingNote || job.latestTimeStamp,
            workspaceName: job.workspaceName || job.workspace || "",
            projectName: job.projectName || job.project || "",
            projectId: job.projectId || job.project || "",
            isLoadingNotes: false,
            hasLoadedNotes: false,
            notes: null,
            errorLoadingNotes: null,
          }));
          setStackedJobs(jobs.filter((j) => j.noteCount > 0));
          return;
        }

        const params = new URLSearchParams({
          searchText: activeSearchTerm,
          userId: userid,
        });
        const jobResponse = await fetch(
          `${apiUrl}/SiteNote/GetJobsBySiteNoteText?${params.toString()}`,
        );
        if (!jobResponse.ok)
          throw new Error(`Failed to fetch: ${jobResponse.status}`);
        const jobData = await jobResponse.json();
        const jobs = (jobData.jobs || []).map((job) => ({
          jobId: job.jobId,
          jobName: job.jobName,
          jobDescription: job.jobDescription || "",
          noteCount: job.matchingNotesCount || job.totalSiteNotes || 0,
          latestTimeStamp: job.latestMatchingNote || job.latestTimeStamp,
          workspaceName: job.workspaceName || job.workspace || "",
          projectName: job.projectName || job.project || "",
          projectId: job.projectId || job.project || "",
          isLoadingNotes: false,
          hasLoadedNotes: false,
          notes: null,
          errorLoadingNotes: null,
        }));
        setStackedJobs(jobs.filter((j) => j.noteCount > 0));
        return;
      }

      const jobResponse = await fetch(
        `${apiUrl}/SiteNote/GetStackedJobs?userId=${userid}&pageNumber=1&pageSize=50`,
      );
      if (!jobResponse.ok)
        throw new Error(`Failed to fetch: ${jobResponse.status}`);
      const jobData = await jobResponse.json();
      if (!jobData.jobs || !Array.isArray(jobData.jobs)) {
        setStackedJobs([]);
        return;
      }
      const jobsWithMetadata = (jobData.jobs || []).map((job) => ({
        jobId: job.jobId,
        jobName: job.jobName,
        jobDescription: job.jobDescription || "",
        projectId: job.projectId || "",
        projectName: job.projectName || "",
        workspaceId: job.workspaceId || "",
        workspaceName: job.workspaceName || "",
        noteCount: job.totalSiteNotes || 0,
        latestTimeStamp: job.latestTimeStamp,
        lastSiteNoteUserName: job.lastSiteNoteUserName || null,
        lastSiteNote: job.lastSiteNote || "",
        isLoadingNotes: false,
        hasLoadedNotes: false,
        notes: null,
        errorLoadingNotes: null,
      }));
      jobsWithMetadata.sort((a, b) => {
  const dateA = new Date(a.latestTimeStamp || 0);
  const dateB = new Date(b.latestTimeStamp || 0);
  return dateB - dateA;
});
      setStackedJobs(jobsWithMetadata.filter((job) => job.noteCount > 0));
    } catch (error) {
      console.error("Error in fetchStackedJobs:", error);
      toast.error("Failed to load stacked jobs");
      setStackedJobs([]);
    } finally {
      setLoadingStackedJobs(false);
    }
  }, [
    apiUrl,
    userid,
    activeSearchTerm,
    viewMode,
    selectedFilters,
    hasActiveFilters,
    generateFilterCombinations,
  ]);

  const fetchWorkspaceRole = useCallback(async () => {
    if (!userid || !defaultUserWorkspaceID) {
      setDefaultWorkspaceRole(null);
      return;
    }
    setLoadingWorkspaceRole(true);
    try {
      const response = await fetch(
        `${apiUrl}/Workspace/GetWorkspacesByUserId/${userid}`,
      );
      if (!response.ok) throw new Error(`Failed: ${response.status}`);
      const data = await response.json();
      const workspaces = data.workspaces || [];
      const defaultWorkspace = workspaces.find(
        (workspace) =>
          (workspace.id || workspace.workspaceId)?.toString() ===
          defaultUserWorkspaceID?.toString(),
      );
      if (defaultWorkspace)
        setDefaultWorkspaceRole(defaultWorkspace.role || null);
      else setDefaultWorkspaceRole(null);
    } catch (error) {
      console.error("Error fetching workspace role:", error);
      setDefaultWorkspaceRole(null);
    } finally {
      setLoadingWorkspaceRole(false);
    }
  }, [apiUrl, userid, defaultUserWorkspaceID]);

  /*   const displayNotes = useMemo(() => {
    if (activeSearchTerm.trim()) return searchResults;
    if (hasActiveFilters) return filteredNotesFromApi;
    return notes || [];
  }, [
    activeSearchTerm,
    searchResults,
    hasActiveFilters,
    filteredNotesFromApi,
    notes,
  ]); */

  const finalDisplayNotes = useMemo(() => {
    if (activeSearchTerm.trim()) return searchResults;
    if (hasActiveFilters) return filteredNotesFromApi;
    return notes || [];
  }, [
    notes,
    hasActiveFilters,
    filteredNotesFromApi,
    searchResults,
    activeSearchTerm,
  ]);

  const fetchInlineImages = useCallback(
    async (noteId) => {
      if (!noteId || loadingImages[noteId]) return;
      setLoadingImages((prev) => ({ ...prev, [noteId]: true }));
      try {
        const response = await fetch(
          `${apiUrl}/InlineImages/GetInlineImagesBySiteNote?siteNoteId=${noteId}`,
        );
        if (response.ok) {
          const data = await response.json();
          const images = data.images || [];
          setInlineImagesMap((prev) => ({ ...prev, [noteId]: images }));
          return images;
        } else {
          setInlineImagesMap((prev) => ({ ...prev, [noteId]: [] }));
          return [];
        }
      } catch (error) {
        console.error(`Error fetching images for note ${noteId}:`, error);
        setInlineImagesMap((prev) => ({ ...prev, [noteId]: [] }));
        return [];
      } finally {
        setLoadingImages((prev) => ({ ...prev, [noteId]: false }));
      }
    },
    [apiUrl, loadingImages],
  );

  const handleFilterByProject = useCallback(
    async (projectId, projectName) => {
      if (!projectId) return;
      const newFilters = {
        date: [],
        workspace: [],
        project: [projectId],
        job: [],
        userName: [],
        task: [],
      };
      setSelectedFilters(newFilters);
      localStorage.setItem(
        "dashboardSelectedFilters",
        JSON.stringify(newFilters),
      );
      if (activeSearchTerm) {
        setActiveSearchTerm("");
        setSearchTerm("");
      }
      fetchNotesWithFilters(newFilters);
      if (viewMode === "stacked") setExpandedStacks({});
      toast.success(`Filtered by project: ${projectName}`);
    },
    [setSelectedFilters, fetchNotesWithFilters, activeSearchTerm, viewMode],
  );

  const handleFilterByWorkspace = useCallback(
    async (workspaceId, workspaceName) => {
      if (!workspaceId) return;
      const newFilters = {
        date: [],
        workspace: [workspaceId],
        project: [],
        job: [],
        userName: [],
        task: [],
      };
      setSelectedFilters(newFilters);
      localStorage.setItem(
        "dashboardSelectedFilters",
        JSON.stringify(newFilters),
      );
      if (activeSearchTerm) {
        setActiveSearchTerm("");
        setSearchTerm("");
      }
      fetchNotesWithFilters(newFilters);
      if (viewMode === "stacked") setExpandedStacks({});
      toast.success(`Filtered by workspace: ${workspaceName}`);
    },
    [setSelectedFilters, fetchNotesWithFilters, activeSearchTerm, viewMode],
  );

  const handleFilterByJob = useCallback(
    async (jobId, jobName) => {
      if (!jobId) return;
      const newFilters = {
        date: [],
        workspace: [],
        project: [],
        job: [jobId],
        userName: [],
        task: [],
      };
      setSelectedFilters(newFilters);
      localStorage.setItem(
        "dashboardSelectedFilters",
        JSON.stringify(newFilters),
      );
      if (activeSearchTerm) {
        setActiveSearchTerm("");
        setSearchTerm("");
      }
      fetchNotesWithFilters(newFilters);
      if (viewMode === "stacked") setExpandedStacks({});
      toast.success(`Filtered by job: ${jobName}`);
    },
    [setSelectedFilters, fetchNotesWithFilters, activeSearchTerm, viewMode],
  );

  const handleFilterByTask = useCallback(
    async (taskId, taskName, job) => {
      if (!taskId || !job) return;
      const newFilters = {
        date: [],
        workspace: [],
        project: [],
        job: [],
        userName: [],
        task: [taskId],
      };
      setSelectedFilters(newFilters);
      localStorage.setItem(
        "dashboardSelectedFilters",
        JSON.stringify(newFilters),
      );
      if (activeSearchTerm) {
        setActiveSearchTerm("");
        setSearchTerm("");
      }
      fetchNotesWithFilters(newFilters);
      if (viewMode === "stacked") {
        setTimeout(async () => {
          const jobsSource = hasActiveFilters
            ? filteredStackedJobs
            : stackedJobs;
          const targetJob = jobsSource.find((j) => j.jobId === job.jobId);
          if (targetJob) {
            await toggleStackExpansion(targetJob.jobName, targetJob.jobId);
            setTimeout(() => {
              setHighlightedTaskId(taskId);
              window.dispatchEvent(
                new CustomEvent("highlightTask", {
                  detail: { taskId, jobId: job.jobId },
                }),
              );
            }, 500);
          }
        }, 300);
      }
      toast.success(`Filtered by Task: ${taskName}`);
    },
    [
      setSelectedFilters,
      fetchNotesWithFilters,
      activeSearchTerm,
      viewMode,
      hasActiveFilters,
      filteredStackedJobs,
      stackedJobs,
      toggleStackExpansion,
    ],
  );

  const handleImageThumbnailClick = useCallback(
    async (note, imageIndex = 0) => {
      if (!note.inlineImageCount || note.inlineImageCount <= 0) return;
      const existingImages = inlineImagesMap[note.id] || [];
      let images = existingImages;
      if (existingImages.length === 0) {
        setLoadingImages((prev) => ({ ...prev, [note.id]: true }));
        try {
          const fetchedImages = await fetchInlineImages(note.id);
          images = fetchedImages;
          if (images.length > 0) {
            setSelectedImageNote(note);
            setSelectedImage({
              image: images[imageIndex],
              index: imageIndex,
              total: images.length,
            });
            setShowImageViewer(true);
          }
        } catch (error) {
          console.error("Error loading images:", error);
          toast.error("Failed to load images");
        }
      } else {
        setSelectedImageNote(note);
        setSelectedImage({
          image: images[imageIndex],
          index: imageIndex,
          total: images.length,
        });
        setShowImageViewer(true);
      }
    },
    [inlineImagesMap, fetchInlineImages],
  );

  const handleNextImage = useCallback(() => {
    if (selectedImage && selectedImageNote) {
      const images = inlineImagesMap[selectedImageNote.id] || [];
      if (images.length > 0) {
        const nextIndex = (selectedImage.index + 1) % images.length;
        setSelectedImage({
          image: images[nextIndex],
          index: nextIndex,
          total: images.length,
        });
      }
    }
  }, [selectedImage, selectedImageNote, inlineImagesMap]);

  const handlePrevImage = useCallback(() => {
    if (selectedImage && selectedImageNote) {
      const images = inlineImagesMap[selectedImageNote.id] || [];
      if (images.length > 0) {
        const prevIndex =
          (selectedImage.index - 1 + images.length) % images.length;
        setSelectedImage({
          image: images[prevIndex],
          index: prevIndex,
          total: images.length,
        });
      }
    }
  }, [selectedImage, selectedImageNote, inlineImagesMap]);

  const getFilterOptions = useCallback(
    (filterType) => {
      const options = filterOptions[filterType] || [];
      if (filterSearchTerm) {
        return options.filter(
          (option) =>
            option.displayText &&
            option.displayText
              .toLowerCase()
              .includes(filterSearchTerm.toLowerCase()),
        );
      }
      return options;
    },
    [filterOptions, filterSearchTerm],
  );

  const getFilterDisplayValue = useCallback(
    (filterType, value) => {
      if (!value) return "";
      const options = filterOptions[filterType] || [];
      const option = options.find(
        (opt) =>
          opt.id?.toString() === value?.toString() ||
          opt.text?.toString() === value?.toString(),
      );
      if (option) return option.displayText || option.text || value;
      if (filterType === "workspace" && workspaces) {
        const workspace = workspaces.find(
          (w) =>
            w.id?.toString() === value?.toString() ||
            w.name?.toString() === value?.toString(),
        );
        if (workspace) return workspace.name || value;
      }
      if (filterType === "project" && projects) {
        const project = projects.find(
          (p) =>
            p.id?.toString() === value?.toString() ||
            p.name?.toString() === value?.toString(),
        );
        if (project) return project.name || value;
      }
      if (filterType === "job" && jobs) {
        const job = jobs.find(
          (j) =>
            j.id?.toString() === value?.toString() ||
            j.name?.toString() === value?.toString(),
        );
        if (job) return job.name || value;
      }
      if (filterType === "date") {
        try {
          let dateStr = value;
          if (value.includes("T")) dateStr = value.split("T")[0];
          const date = new Date(dateStr);
          if (!isNaN(date.getTime()))
            return date.toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            });
        } catch (e) {
          console.error("Error formatting date:", e);
        }
      }
      return value;
    },
    [filterOptions, workspaces, projects, jobs],
  );

    const debouncedSaveFilters = useCallback((filters) => {
    if (saveFiltersDebounceRef.current) {
      clearTimeout(saveFiltersDebounceRef.current);
    }
    saveFiltersDebounceRef.current = setTimeout(() => {
      localStorage.setItem("dashboardSelectedFilters", JSON.stringify(filters));
    }, 300);
  }, []);

const handleFilterCheckboxChange = useCallback(
  (filterType, value, rawValue = null) => {
    setSelectedFilters(prevFilters => {
      const currentValues = prevFilters[filterType] || [];
      const newValues = currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value];
      const newFilters = { ...prevFilters, [filterType]: newValues };
      
      debouncedSaveFilters(newFilters);
      
      // Defer the expensive side effects
      startFilterTransition(() => {
        const hasActive = Object.values(newFilters).some(arr => arr.length > 0);
        if (hasActive) {
          debouncedFetchNotesWithFilters(newFilters);
          if (viewMode === "stacked") setExpandedStacks({});
        } else {
          setFilteredNotesFromApi(notes || []);
          setFilteredStackedJobs([]);
          if (viewMode === "stacked") fetchStackedJobs();
        }
      });
      
      return newFilters;
    });
  },
  [notes, debouncedFetchNotesWithFilters, viewMode, fetchStackedJobs, startFilterTransition, debouncedSaveFilters]
);
  const refreshStackedViewWithNewNote = useCallback(
    async (noteData, operation = "add") => {
      if (!noteData || viewMode !== "stacked") return;
      if (hasActiveFilters) await fetchNotesWithFilters(selectedFilters);
      else {
        const jobId = noteData.jobId;
        setStackedJobs((prev) => {
          const jobIndex = prev.findIndex(
            (j) => j.jobId?.toString() === jobId?.toString(),
          );
          if (jobIndex === -1) return prev;
          const updated = [...prev];
          const job = { ...updated[jobIndex] };
          if (operation === "add") {
            job.noteCount += 1;
            const noteTime = new Date(
              noteData.timeStamp || noteData.date,
            ).getTime();
            const currentLatest = new Date(job.latestTimeStamp || 0).getTime();
            if (noteTime > currentLatest) {
              job.latestTimeStamp = noteData.timeStamp || noteData.date;
              job.lastSiteNote = noteData.note || "";
              job.lastSiteNoteUserName = noteData.userName || noteData.UserName;
            }
            if (expandedStacks[job.jobName] && job.hasLoadedNotes)
              job.notes = [noteData, ...(job.notes || [])];
          } else if (operation === "update") {
            if (job.lastSiteNoteUserName === noteData.userName)
              job.lastSiteNote = noteData.note || "";
            if (
              expandedStacks[job.jobName] &&
              job.hasLoadedNotes &&
              job.notes
            ) {
              const noteIndex = job.notes.findIndex(
                (n) => n.id === noteData.id,
              );
              if (noteIndex !== -1)
                job.notes = [
                  ...job.notes.slice(0, noteIndex),
                  noteData,
                  ...job.notes.slice(noteIndex + 1),
                ];
            }
          }
          updated[jobIndex] = job;
          return updated;
        });
      }
    },
    [
      viewMode,
      hasActiveFilters,
      selectedFilters,
      expandedStacks,
      fetchNotesWithFilters,
    ],
  );

 const removeFilter = useCallback(
  (filterType, value) => {
    setSelectedFilters(prevFilters => {
      const currentValues = prevFilters[filterType] || [];
      const newValues = currentValues.filter(v => v !== value);
      const newFilters = { ...prevFilters, [filterType]: newValues };
      
      debouncedSaveFilters(newFilters);
      
      startFilterTransition(() => {
        const hasActive = Object.values(newFilters).some(arr => arr.length > 0);
        if (hasActive) {
          fetchNotesWithFilters(newFilters);
          if (viewMode === "stacked") setExpandedStacks({});
        } else {
          setFilteredNotesFromApi(notes || []);
          setFilteredStackedJobs([]);
          if (viewMode === "stacked") fetchStackedJobs();
        }
      });
      
      return newFilters;
    });
  },
  [notes, fetchNotesWithFilters, viewMode, fetchStackedJobs, startFilterTransition, debouncedSaveFilters]
);


  const clearAllFilters = useCallback(async () => {
    setIsClearingFilters(true);
    try {
      const emptyFilters = {
        date: [],
        workspace: [],
        project: [],
        job: [],
        userName: [],
        task: [],
      };
      setSelectedFilters(emptyFilters);
      setFilteredNotesFromApi([]);
      setFilteredStackedJobs([]);
      setExpandedStacks({});
      localStorage.removeItem("dashboardSelectedFilters");
      // ✅ No preloading of filter options
      if (viewMode === "stacked") await fetchStackedJobs();
      refreshNotes();
      toast.success("All filters cleared");
    } catch (error) {
      console.error("Error clearing filters:", error);
      toast.error("Failed to clear filters");
    } finally {
      setIsClearingFilters(false);
    }
  }, [viewMode, fetchStackedJobs, refreshNotes]);
  // ========== END MODIFIED ==========

  const toggleFilterDropdown = useCallback(
    async (filterType) => {
      if (filterDropdownOpen !== filterType)
        await fetchFilterOptions(filterType);
      if (filterDropdownOpen === filterType) {
        setFilterDropdownOpen(null);
        setFilterSearchTerm("");
      } else {
        setFilterDropdownOpen(filterType);
        setFilterSearchTerm("");
      }
    },
    [filterDropdownOpen, fetchFilterOptions],
  );

  const getFilterButtonLabel = (filterType) => {
    const count = selectedFilters[filterType]?.length || 0;
    const isLoading = loadingFilterOptions[filterType];
    const baseLabels = {
      date: "Date",
      workspace: "Workspace",
      project: "Project",
      job: "Job",
      userName: "User",
      task: "Task",
    };
    const iconMap = {
      date: "calendar",
      workspace: "building",
      project: "project-diagram",
      job: "tasks",
      userName: "user",
      task: "list-check",
    };
    const hasOtherFilters = Object.entries(selectedFilters).some(
      ([key, values]) => key !== filterType && values.length > 0,
    );
    return (
      <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
        {isLoading ? (
          <i
            className="fas fa-spinner fa-spin"
            style={{ fontSize: "14px", color: "#3498db" }}
          />
        ) : (
          <i
            className={`fas fa-${iconMap[filterType]}`}
            style={{
              fontSize: "14px",
              color: hasOtherFilters ? "#17a2b8" : "#3498db",
            }}
          />
        )}
        {isLoading
          ? `Loading ${baseLabels[filterType]}...`
          : baseLabels[filterType]}
        {count > 0 && ` (${count})`}
        {hasOtherFilters && !isLoading && (
          <span
            title="Filtered by other selections"
            style={{ marginLeft: "4px", fontSize: "10px" }}
          >
            <i className="fas fa-filter" style={{ color: "#17a2b8" }} />
          </span>
        )}
      </span>
    );
  };

  const getActiveFilterCount = () =>
    Object.values(selectedFilters).reduce(
      (total, arr) => total + arr.length,
      0,
    );

  const ensureFilterOptionsLoaded = useCallback(
    async (filterType) => {
      if (!filterOptionsLoaded[filterType] && !loadingFilterOptions[filterType])
        await fetchFilterOptions(filterType);
    },
    [filterOptionsLoaded, loadingFilterOptions, fetchFilterOptions],
  );

  const renderActiveFilters = () => {
    const activeFilters = [];
    Object.entries(selectedFilters).forEach(([filterType, values]) => {
      if (values.length > 0) {
        values.forEach((value) => {
          if (
            !filterOptionsLoaded[filterType] &&
            !loadingFilterOptions[filterType]
          )
            ensureFilterOptionsLoaded(filterType);
          const displayValue = getFilterDisplayValue(filterType, value);
          activeFilters.push({ filterType, value, displayValue });
        });
      }
    });
    if (activeFilters.length === 0) return null;
    return (
      <div className="active-filters-container">
        <div className="active-filters-title">
          <i
            className="fas fa-filter"
            style={{ fontSize: "14px", color: "#495057" }}
          />
          Active Filters ({getActiveFilterCount()}):
        </div>
        <div className="active-filters-content">
          {activeFilters.map((filter, index) => (
            <div
              key={`${filter.filterType}-${filter.value}-${index}`}
              className="filter-tag"
            >
              <span style={{ fontWeight: "500", color: "#1976d2" }}>
                {filter.filterType === "date"
                  ? "Date"
                  : filter.filterType === "workspace"
                    ? "Workspace"
                    : filter.filterType === "project"
                      ? "Project"
                      : filter.filterType === "job"
                        ? "Job"
                        : "User"}
                : {filter.displayValue}
              </span>
              <button
                onClick={() => removeFilter(filter.filterType, filter.value)}
                className="filter-tag-remove"
                title="Remove filter"
              >
                <i className="fas fa-times" />
              </button>
            </div>
          ))}
          {activeFilters.length > 0 && (
            <button onClick={clearAllFilters} className="clear-all-button">
              <i className="fas fa-times-circle" />
              Clear All
            </button>
          )}
        </div>
      </div>
    );
  };

  // Combined localStorage save effect
  useEffect(() => {
    localStorage.setItem("dashboardSearchTerm", searchTerm);
    localStorage.setItem(
      "dashboardSearchResults",
      JSON.stringify(searchResults),
    );
    localStorage.setItem("dashboardViewMode", viewMode);

    const filtersToSave = {
      date: selectedFilters.date || [],
      workspace: selectedFilters.workspace || [],
      project: selectedFilters.project || [],
      job: selectedFilters.job || [],
      task: selectedFilters.task || [],
      userName: selectedFilters.userName || [],
    };
    localStorage.setItem(
      "dashboardSelectedFilters",
      JSON.stringify(filtersToSave),
    );
  }, [searchTerm, searchResults, viewMode, selectedFilters]);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setFilterDropdownOpen(null);
        setFilterSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Mobile check
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const loadMoreCards = (jobName) =>
    setExpandedCardLimit((prev) => ({
      ...prev,
      [jobName]: (prev[jobName] ?? 50) + 10,
    }));

  const handleViewAttachments = useCallback((note) => {
    setSelectedFileNote(note);
    setShowAttachedFileModal(true);
  }, []);

  const handleRowDoubleClick = useCallback(
    (note) => {
      let job = jobs.find((j) => j.name && note.job && j.name === note.job);
      if (!job && note.jobId)
        job = jobs.find(
          (j) => (j.id || j.jobId)?.toString() === note.jobId.toString(),
        );
      setViewNote({ id: note.id, jobId: job?.id ?? null });
      setShowViewModal(true);
    },
    [jobs],
  );

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const response = await fetch(
          `${apiUrl}/UserManagement/GetUserById/${userid}`,
        );
        if (response.ok) {
          const data = await response.json();
          setCurrentUser(data.user);
        }
      } catch (error) {
        console.error("Error fetching user:", error);
      }
    };
    if (userid) fetchCurrentUser();
  }, [userid, apiUrl]);

  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now - date;
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes} min ago`;
    if (diffInHours < 24)
      return `${diffInHours} hour${diffInHours > 1 ? "s" : ""} ago`;
    if (diffInDays === 1) return "Yesterday";
    if (diffInDays < 7)
      return `${diffInDays} day${diffInDays > 1 ? "s" : ""} ago`;
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const prevFiltersRef = useRef(selectedFilters);
  useEffect(() => {
    const prevFilters = prevFiltersRef.current;
    const hasChanged =
      JSON.stringify(prevFilters) !== JSON.stringify(selectedFilters);
    if (hasChanged && hasActiveFilters)
      debouncedFetchNotesWithFilters(selectedFilters);
    else if (hasChanged && !hasActiveFilters) {
      setFilteredNotesFromApi([]);
      setFilteredStackedJobs([]);
    }
    prevFiltersRef.current = selectedFilters;
  }, [
    hasActiveFilters,
    selectedFilters,
    debouncedFetchNotesWithFilters,
    notes,
  ]);

  useEffect(
    () => () => {
      if (fetchNotesTimeoutRef.current)
        clearTimeout(fetchNotesTimeoutRef.current);
    },
    [],
  );

  useEffect(() => {
    if (userid && defaultUserWorkspaceID) {
      fetchWorkspaceRole();
      fetchWorkspacesByUserId();
    }
  }, [userid, defaultUserWorkspaceID, fetchWorkspaceRole]);
  useEffect(() => {
    if (notes !== undefined) {
      setIsDataLoaded(true);
      setInitialLoading(false);
    }
  }, [notes]);

  useEffect(() => {
    if (viewMode === "stacked") {
      // Conditions for when to fetch:
      // 1. No active filters and userid exists
      // 2. Or activeSearchTerm exists (with or without filters)
      // 3. Or hasActiveFilters with activeSearchTerm
      if (
        (userid && !hasActiveFilters) ||
        (activeSearchTerm && activeSearchTerm.trim()) ||
        (hasActiveFilters && activeSearchTerm && activeSearchTerm.trim())
      ) {
        fetchStackedJobs();
      }
    }
  }, [
    viewMode,
    userid,
    hasActiveFilters,
    activeSearchTerm,
    selectedFilters,
    fetchStackedJobs,
  ]);

  const handlePriorityUpdate = () => refreshNotes();

  const handlePriorityChange = async (priorityValue) => {
    if (!selectedNoteForPriority) return;
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const noteId = selectedNoteForPriority.id;
      const updatePriority = async () => {
        try {
          const getPriorityUrl = `${apiUrl}/Priority/GetPriorityByNoteId/${noteId}`;
          const getResponse = await fetch(getPriorityUrl);
          if (getResponse.ok) {
            const priorityData = await getResponse.json();
            const priorityId = priorityData.priority?.id;
            if (priorityId) {
              const updateResponse = await fetch(
                `${apiUrl}/Priority/UpdatePriority/${priorityId}`,
                {
                  method: "PUT",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ priorityValue }),
                },
              );
              if (updateResponse.ok)
                return {
                  success: true,
                  message: "Priority updated successfully",
                };
            }
          }
        } catch (error) {
          console.log("No existing priority found, will create new");
        }
        const createResponse = await fetch(`${apiUrl}/Priority/AddPriority`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            noteID: noteId,
            priorityValue,
            userId: user.id,
          }),
        });
        if (createResponse.ok)
          return { success: true, message: "Priority created successfully" };
        throw new Error("Failed to create priority");
      };
      const result = await updatePriority();
      if (result.success) {
        toast.success(result.message);
        await refreshNotes();
        if (viewMode === "stacked") {
          if (hasActiveFilters) fetchNotesWithFilters(selectedFilters);
          else await fetchStackedJobs();
        }
      }
    } catch (error) {
      console.error("Error updating priority:", error);
      toast.error("Failed to update priority. Please try again.");
    } finally {
      setShowPriorityDropdown(false);
      setSelectedNoteForPriority(null);
    }
  };

  const handleRowClick = useCallback((note) => {
    setFocusedRow(note.id);
    setSelectedRow(note.id);
  }, []);

  const handleKeyDown = useCallback(
    (event) => {
      if (!focusedRow) return;
      const currentIndex = finalDisplayNotes.findIndex(
        (note) => note.id === focusedRow,
      );
      if (event.key === "ArrowDown") {
        event.preventDefault();
        const nextIndex = Math.min(
          currentIndex + 1,
          finalDisplayNotes.length - 1,
        );
        if (nextIndex !== currentIndex) {
          setFocusedRow(finalDisplayNotes[nextIndex].id);
          setSelectedRow(finalDisplayNotes[nextIndex].id);
        }
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        const prevIndex = Math.max(currentIndex - 1, 0);
        if (prevIndex !== currentIndex) {
          setFocusedRow(finalDisplayNotes[prevIndex].id);
          setSelectedRow(finalDisplayNotes[prevIndex].id);
        }
      } else if (event.ctrlKey && event.key === "a" && focusedRow) {
        event.preventDefault();
        const selectedNote = finalDisplayNotes.find(
          (note) => note.id === focusedRow,
        );
        if (selectedNote) {
          const today = new Date();
          const year = today.getFullYear();
          const month = String(today.getMonth() + 1).padStart(2, "0");
          const day = String(today.getDate()).padStart(2, "0");
          setPrefilledData({
            date: `${year}-${month}-${day}`,
            project: selectedNote.project,
            projectId: selectedNote.projectId,
            job: selectedNote.job,
            jobId: selectedNote.jobId,
            workspace: selectedNote.workspace,
            taskid: selectedNote.taskId,
          });
          setModalSource("grid");
          setShowNewModal(true);
        }
      }
    },
    [focusedRow, finalDisplayNotes],
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  const handleRefresh = async () => {
    setInitialLoading(true);
    const currentExpandedStacks = { ...expandedStacks };
    const currentViewMode = viewMode;
    try {
      await refreshNotes();
      setFilterOptionsLoaded({
        date: false,
        workspace: false,
        project: false,
        job: false,
        userName: false,
      });
      await reloadAllFilters();
      if (currentViewMode === "stacked") {
        if (hasActiveFilters) {
          await fetchNotesWithFilters(selectedFilters);
          setTimeout(() => {
            setFilteredStackedJobs((prev) =>
              prev.map((job) => {
                const wasExpanded = Object.keys(currentExpandedStacks).some(
                  (jobName) =>
                    jobName === job.jobName && currentExpandedStacks[jobName],
                );
                if (wasExpanded)
                  setTimeout(
                    () => toggleStackExpansion(job.jobName, job.jobId),
                    100,
                  );
                return job;
              }),
            );
          }, 300);
        } else {
          await fetchStackedJobs();
          setTimeout(() => {
            setStackedJobs((prev) => {
              const updated = [...prev];
              Object.keys(currentExpandedStacks).forEach((jobName) => {
                if (currentExpandedStacks[jobName]) {
                  const jobIndex = updated.findIndex(
                    (j) => j.jobName === jobName,
                  );
                  if (jobIndex !== -1)
                    setTimeout(
                      () =>
                        toggleStackExpansion(
                          updated[jobIndex].jobName,
                          updated[jobIndex].jobId,
                        ),
                      200,
                    );
                }
              });
              return updated;
            });
          }, 300);
        }
      }
      const filterTypes = [
        "date",
        "workspace",
        "project",
        "job",
        "userName",
        "task",
      ];
      filterTypes.forEach((type) => fetchFilterOptions(type));
      toast.success("Refreshed successfully");
    } catch (error) {
      console.error("Refresh error:", error);
      toast.error("Refresh failed");
    } finally {
      setInitialLoading(false);
    }
  };

  const handleAddFromRow = (note) => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    setPrefilledData({
      date: `${year}-${month}-${day}`,
      project: note.project,
      projectId: note.projectId,
      job: note.job || note.jobName,
      jobId: note.jobId,
      workspace: note.workspace,
      userId: note.userId,
      taskid: note.taskId || note.id,
      taskName: note.title,
    });
    setModalSource("grid");
    setShowNewModal(true);
  };

  const handleNewNoteClick = () => {
    setPrefilledData(null);
    setModalSource("dashboard");
    setShowNewModal(true);
  };
  const handleNewTaskClick = () => setShowNewTaskModal(true);

  useEffect(() => {
    if (showNewModal && modalSource === "grid" && newNoteModalRef.current) {
      const timer = setTimeout(() => {
        if (newNoteModalRef.current) newNoteModalRef.current.focusTextarea();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [showNewModal, modalSource]);

  const handleEdit = (note) => {
    if (note.itemType === "Task") {
      setEditingTask(note);
      setShowNewTaskModal(true);
    } else {
      setSelectedNote(note);
      setShowEditModal(true);
    }
  };

  const handleDelete = (note) => {
    const currentUser = JSON.parse(localStorage.getItem("user"));
    if (note.userId && Number(note.userId) !== Number(currentUser.id)) {
      toast.error("You can only delete notes that you created.");
      return;
    }
    const hrs = (Date.now() - new Date(note.timeStamp)) / 36e5;
    if (hrs > 24) toast.error("Cannot delete notes older than 24 hours");
    else {
      setNoteToDelete(note);
      setShowDeleteConfirm(true);
    }
  };

  const deleteInlineImages = async (noteId) => {
    try {
      const imagesResponse = await fetch(
        `${apiUrl}/InlineImages/GetInlineImagesBySiteNote?siteNoteId=${noteId}`,
      );
      if (imagesResponse.ok) {
        const imagesData = await imagesResponse.json();
        const images = imagesData.images || [];
        for (const image of images)
          await fetch(`${apiUrl}/InlineImages/DeleteInlineImage/${image.id}`, {
            method: "DELETE",
          });
        return true;
      } else return false;
    } catch (error) {
      console.error("Error deleting inline images:", error);
      return false;
    }
  };

  const deleteAttachedDocuments = async (noteId) => {
    try {
      const docsResponse = await fetch(
        `${apiUrl}/Documents/GetDocumentMetadataByReference?siteNoteId=${noteId}`,
      );
      if (docsResponse.ok) {
        const docsData = await docsResponse.json();
        const documents = docsData.documents || [];
        for (const document of documents)
          await fetch(`${apiUrl}/Documents/DeleteDocument/${document.id}`, {
            method: "DELETE",
          });
        return true;
      } else return false;
    } catch (error) {
      console.error("Error deleting documents:", error);
      return false;
    }
  };

  const handleConfirmDelete = async () => {
    if (!noteToDelete) return;
    setIsDeleting(true);
    try {
      const imagesDeleted = await deleteInlineImages(noteToDelete.id);
      if (!imagesDeleted) {
        toast.error("Failed to delete inline images. Please try again.");
        setIsDeleting(false);
        return;
      }
      const documentsDeleted = await deleteAttachedDocuments(noteToDelete.id);
      if (!documentsDeleted) {
        toast.error("Failed to delete attached documents. Please try again.");
        setIsDeleting(false);
        return;
      }
      const url = new URL(
        `${apiUrl}/SiteNote/DeleteSiteNote/${noteToDelete.id}`,
      );
      url.searchParams.append("userId", userid);
      const response = await fetch(url.toString(), {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      });
      if (!response.ok)
        throw new Error((await response.text()) || "Delete failed");
      toast.success("Note and associated files deleted successfully");
      await refreshNotes();
      if (viewMode === "stacked") {
        if (hasActiveFilters) {
          await fetchNotesWithFilters(selectedFilters);
        } else {
          await fetchStackedJobs();
          await fetchNotesForTask(noteToDelete.taskId, noteToDelete.jobId);
        }
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error(
        error.message || "Failed to delete note and associated files",
        error.message || "Failed to delete note and associated files",
      );
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
      setNoteToDelete(null);
    }
  };

  const performSearch = useCallback(
    async (searchText) => {
      const trimmedSearch = searchText.trim();
      if (!trimmedSearch || !userid) {
        setSearchResults([]);
        setSearchLoading(false);
        return;
      }
      setSearchLoading(true);
      try {
        const hasFilters = Object.values(selectedFilters || {}).some(
          (arr) => arr?.length > 0,
        );
        let combinedNotes = [];
        const seen = new Set();
        if (hasFilters) {
          const combinations = filterCombinations;
          const promises = combinations.map((combo) => {
            const params = new URLSearchParams({
              pageNumber: "1",
              pageSize: "50",
              searchText: trimmedSearch,
              userId: userid,
            });
            if (combo.date) params.append("date", combo.date);
            if (combo.workspace) params.append("workspaceId", combo.workspace);
            if (combo.project) params.append("projectId", combo.project);
            if (combo.job) params.append("jobId", combo.job);
            if (combo.userName) params.append("username", combo.userName);
            if (combo.task) params.append("taskId", combo.task);
            return fetch(
              `${apiUrl}/SiteNote/GetSiteNotesWithTextFilter?${params.toString()}`,
            ).then((res) =>
              res.ok ? res.json() : { siteNotes: [], data: [] },
            );
          });
          const results = await Promise.all(promises);
          results.forEach((r) => {
            (r.data || r.siteNotes || []).forEach((n) => {
              if (!seen.has(n.id)) {
                seen.add(n.id);
                combinedNotes.push(n);
              }
            });
          });
        } else {
          const params = new URLSearchParams({
            pageNumber: "1",
            pageSize: "50",
            searchText: trimmedSearch,
            userId: userid,
          });
          const response = await fetch(
            `${apiUrl}/SiteNote/GetSiteNotesWithTextFilter?${params.toString()}`,
          );
          if (!response.ok) throw new Error();
          const data = await response.json();
          combinedNotes = data.data || data.siteNotes || [];
        }
        setSearchResults(
          combinedNotes.map((n) => ({
            ...n,
            userName: n.UserName || n.userName,
            documentCount: n.DocumentCount || n.documentCount || 0,
            note: n.note || n.content || "",
          })),
        );
      } catch (error) {
        toast.error("Search failed");
        setSearchResults([]);
        console.error("Search error:", error);
      } finally {
        setSearchLoading(false);
      }
    },
    [apiUrl, userid, selectedFilters, generateFilterCombinations],
  );

  useEffect(() => {
    if (activeSearchTerm.trim() && hasActiveFilters)
      performSearch(activeSearchTerm);
  }, [selectedFilters, hasActiveFilters, activeSearchTerm, performSearch]);

  const handleSearchKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        setActiveSearchTerm(searchTerm);
        localStorage.setItem("dashboardSearchTerm", searchTerm);
        if (hasActiveFilters) fetchNotesWithFilters(selectedFilters);
        if (viewMode === "stacked") fetchStackedJobs();
      }
    },
    [
      searchTerm,
      hasActiveFilters,
      selectedFilters,
      fetchNotesWithFilters,
      viewMode,
      fetchStackedJobs,
    ],
  );

  const handleClearSearch = useCallback(() => {
    setSearchTerm("");
    setActiveSearchTerm("");
    setSearchResults([]);
    localStorage.removeItem("dashboardSearchTerm");
    localStorage.removeItem("dashboardSearchResults");
  }, []);

  useEffect(() => {
    performSearch(activeSearchTerm);
  }, [activeSearchTerm, performSearch]);
  useEffect(() => {
    setHasActiveSearchText(activeSearchTerm.trim() !== "");
  }, [activeSearchTerm]);
  useEffect(() => {
    if (activeSearchTerm)
      localStorage.setItem("dashboardSearchTerm", activeSearchTerm);
    else localStorage.removeItem("dashboardSearchTerm");
  }, [activeSearchTerm]);
  useEffect(() => {
    localStorage.setItem(
      "dashboardSearchResults",
      JSON.stringify(searchResults),
    );
  }, [searchResults]);
  useEffect(() => {
    if (hasActiveFilters) fetchNotesWithFilters(selectedFilters);
  }, [
    activeSearchTerm,
    hasActiveFilters,
    selectedFilters,
    fetchNotesWithFilters,
  ]);
  useEffect(() => {
    if (hasActiveFilters && activeSearchTerm.trim())
      fetchNotesWithFilters(selectedFilters);
  }, [
    selectedFilters,
    hasActiveFilters,
    activeSearchTerm,
    fetchNotesWithFilters,
  ]);

  const fetchWorkspacesByUserId = async () => {
    try {
      const response = await fetch(
        `${apiUrl}/Workspace/GetActiveWorkspacesNameByUserId/${userid}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json; charset=utf-8" },
        },
      );
      if (response.ok) {
        const data = await response.json();
        setUserWorkspace(data.workspaces || data || []);
      } else console.error("API response not OK:", response.status);
    } catch (error) {
      console.error("Error:", error);
      setRole(null);
    } finally {
      setIsRoleLoading(false);
    }
  };

  const renderStackedImageIcon = (note) => {
    const imageCount = note.inlineImageCount || 0;
    const isLoading = loadingImages[note.id];
    if (!imageCount) return null;
    if (isLoading)
      return (
        <button
          className="image-icon-btn"
          style={{ opacity: 0.5, marginRight: "4px" }}
          title="Loading images..."
        >
          <i className="fas fa-spinner fa-spin" />
        </button>
      );
    return (
      <div
        className="inline-image-icon"
        style={{
          display: "inline-flex",
          alignItems: "center",
          marginRight: "4px",
          position: "relative",
          cursor: "pointer",
        }}
        onClick={(e) => {
          e.stopPropagation();
          handleImageThumbnailClick(note);
        }}
        title={`${imageCount} inline image${imageCount !== 1 ? "s" : ""} - Click to view`}
      >
        <div
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
          }}
        >
          <i
            className="fas fa-image"
            style={{
              fontSize: "18px",
              color: "#3498db",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = "scale(1.2)";
              e.target.style.color = "#2980b9";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "scale(1)";
              e.target.style.color = "#3498db";
            }}
          />
          <div
            style={{
              position: "absolute",
              top: "-5px",
              right: "-5px",
              backgroundColor: "#e74c3c",
              color: "white",
              fontSize: "9px",
              fontWeight: "bold",
              minWidth: "16px",
              height: "16px",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "2px solid white",
              boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
              padding: "0 2px",
            }}
          >
            {imageCount}
          </div>
        </div>
      </div>
    );
  };

  const renderTableImageIcon = (note) => {
    const imageCount = note.inlineImageCount || 0;
    const isLoading = loadingImages[note.id];
    if (!imageCount) return null;
    if (isLoading)
      return (
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            marginLeft: "6px",
          }}
        >
          <i
            className="fas fa-spinner fa-spin"
            style={{ fontSize: "12px", color: "#666" }}
          />
        </div>
      );
    return (
      <div
        className="inline-image-icon"
        style={{
          display: "inline-flex",
          alignItems: "center",
          marginLeft: "6px",
          cursor: "pointer",
          position: "relative",
        }}
        onClick={(e) => {
          e.stopPropagation();
          handleImageThumbnailClick(note);
        }}
        title={`${imageCount} inline image${imageCount !== 1 ? "s" : ""} - Click to view`}
      >
        <div style={{ position: "relative" }}>
          <i
            className="fas fa-image"
            style={{
              fontSize: "16px",
              color: "#3498db",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = "scale(1.2)";
              e.target.style.color = "#2980b9";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "scale(1)";
              e.target.style.color = "#3498db";
            }}
          />
        </div>
      </div>
    );
  };

  const renderCardImageIcon = (note) => {
    const imageCount = note.inlineImageCount || 0;
    const isLoading = loadingImages[note.id];
    if (!imageCount) return null;
    if (isLoading)
      return (
        <button
          className="image-icon-btn"
          style={{ opacity: 0.5 }}
          title="Loading images..."
        >
          <i className="fas fa-spinner fa-spin" />
        </button>
      );
    return (
      <div
        className="inline-image-icon"
        style={{
          display: "inline-flex",
          alignItems: "center",
          marginRight: "4px",
          position: "relative",
          cursor: "pointer",
        }}
        onClick={(e) => {
          e.stopPropagation();
          handleImageThumbnailClick(note);
        }}
        title={`${imageCount} inline image${imageCount !== 1 ? "s" : ""} - Click to view`}
      >
        <div
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
          }}
        >
          <i
            className="fas fa-image"
            style={{
              fontSize: "18px",
              color: "#3498db",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = "scale(1.2)";
              e.target.style.color = "#2980b9";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "scale(1)";
              e.target.style.color = "#3498db";
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="main-content">
      <div className="dashboard">
        <MemoizedDashboardMenu
          defaultUserWorkspaceID={defaultUserWorkspaceID}
          defaultUserWorkspaceName={defaultUserWorkspaceName}
          fetchProjectAndJobs={fetchProjectAndJobs}
          onUpdateDefaultWorkspace={onUpdateDefaultWorkspace}
          workspaces={workspaces}
          userid={userid}
          onLogout={onLogout}
          userRole={userRole}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          handleSearchKeyDown={handleSearchKeyDown}
          handleClearSearch={handleClearSearch}
          searchColumn={searchColumn}
          setSearchColumn={setSearchColumn}
          viewMode={viewMode}
          setViewMode={setViewMode}
          handleRefresh={handleRefresh}
          refreshNotes={refreshNotes}
          handleNewTaskModal={handleNewTaskClick}
          defWorkName={defaultUserWorkspaceName}
          role={role}
          onHighlightNote={handleHighlightNote}
        />

        <MemoizedDashboardHeader
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          handleSearchKeyDown={handleSearchKeyDown}
          handleClearSearch={handleClearSearch}
          searchColumn={searchColumn}
          setSearchColumn={setSearchColumn}
          viewMode={viewMode}
          setViewMode={setViewMode}
          handleRefresh={handleRefresh}
          handleNewNoteClick={handleNewNoteClick}
          isMobile={isMobile}
          mobileFiltersOpen={mobileFiltersOpen}
          setMobileFiltersOpen={setMobileFiltersOpen}
          selectedFilters={selectedFilters}
          filterDropdownOpen={filterDropdownOpen}
          toggleFilterDropdown={toggleFilterDropdown}
          filterSearchTerm={filterSearchTerm}
          setFilterSearchTerm={setFilterSearchTerm}
          handleFilterCheckboxChange={handleFilterCheckboxChange}
          getFilterOptions={getFilterOptions}
          getFilterButtonLabel={getFilterButtonLabel}
          clearAllFilters={clearAllFilters}
          getActiveFilterCount={getActiveFilterCount}
          removeFilter={removeFilter}
          getFilterDisplayValue={getFilterDisplayValue}
          filterRef={filterRef}
          loadingFilterOptions={loadingFilterOptions}
          renderActiveFilters={renderActiveFilters}
          userId={userid}
          onFilterByTask={handleFilterByTask}
          toggleStackExpansion={toggleStackExpansion}
          handleFilterByProject={handleFilterByProject}
          handleFilterByWorkspace={handleFilterByWorkspace}
          handleFilterByJob={handleFilterByJob}
        />

        <MemoizedNotesTab
          viewMode={viewMode}
          finalDisplayNotes={finalDisplayNotes}
          isDataLoaded={isDataLoaded}
          initialLoading={initialLoading}
          searchLoading={searchLoading}
          loadingFiltered={loadingFiltered}
          searchTerm={activeSearchTerm}
          getActiveFilterCount={getActiveFilterCount}
          handleRowClick={handleRowClick}
          handleRowDoubleClick={handleRowDoubleClick}
          handleAddFromRow={handleAddFromRow}
          handleEdit={handleEdit}
          handleDelete={handleDelete}
          handleViewAttachments={handleViewAttachments}
          selectedRow={selectedRow}
          focusedRow={focusedRow}
          inlineImagesMap={inlineImagesMap}
          loadingImages={loadingImages}
          renderTableImageIcon={renderTableImageIcon}
          renderCardImageIcon={renderCardImageIcon}
          renderStackedImageIcon={renderStackedImageIcon}
          handleImageThumbnailClick={handleImageThumbnailClick}
          handlePriorityChange={handlePriorityChange}
          showPriorityDropdown={showPriorityDropdown}
          setShowPriorityDropdown={setShowPriorityDropdown}
          selectedNoteForPriority={selectedNoteForPriority}
          priorityDropdownPosition={priorityDropdownPosition}
          setSelectedNoteForPriority={setSelectedNoteForPriority}
          setPriorityDropdownPosition={setPriorityDropdownPosition}
          expandedStacks={expandedStacks}
          toggleStackExpansion={toggleStackExpansion}
          expandedCardLimit={expandedCardLimit}
          loadMoreCards={loadMoreCards}
          jobs={jobs}
          tasks={tasks}
          setViewNote={setViewNote}
          setShowViewModal={setShowViewModal}
          stackedJobs={hasActiveFilters ? filteredStackedJobs : stackedJobs}
          loadingStackedJobs={loadingStackedJobs}
          fetchStackedJobs={fetchStackedJobs}
          fetchNotes={fetchNotes}
          userId={userid}
          hasActiveFilters={hasActiveFilters}
          filteredNotesFromApi={filteredNotesFromApi}
          searchResults={searchResults}
          handleFilterCheckboxChange={handleFilterCheckboxChange}
          removeFilter={removeFilter}
          selectedFilters={selectedFilters}
          handleFilterChange={handleFilterCheckboxChange}
          projects={projects}
          workspaces={workspaces}
          fetchNotesForTask={fetchNotesForTask}
          loadingNotesByTask={loadingNotesByTask}
          notesByTask={notesByTask}
          fetchTasksForJob={fetchTasksForJob}
          tasksByJob={tasksByJob}
          loadingTasksByJob={loadingTasksByJob}
          isClearingFilter={isClearingFilters}
          onFilterByProject={handleFilterByProject}
          refreshNotes={refreshNotes}
          onFilterByWorkspace={handleFilterByWorkspace}
          highlightedTaskId={highlightedTaskId}
          clearHighlightedTask={clearHighlightedTask}
          onFilterByTask={handleFilterByTask}
          loadMoreTaskNotes={loadMoreTaskNotes}
          taskPagination={taskPagination}
          ref={notesTabRef}
          workspaceRole={workspaceRole}
            stackedState={stackedState}
          setExpandedProjects={setExpandedProjects}
          setExpandedTasks={setExpandedTasks}
          setActiveTabByJob={setActiveTabByJob}
          setAssigneeFilters={setAssigneeFilters}
        />
      </div>

      {showDeleteConfirm && (
        <div
          className="modal-overlay"
          onClick={() => setShowDeleteConfirm(false)}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ padding: 24, maxWidth: 400 }}
          >
            <i
              className="fas fa-exclamation-triangle"
              style={{ fontSize: 48, color: "#f39c12" }}
            />
            <h3>Confirm Delete</h3>
            <p>
              Are you sure you want to delete this note and all associated
              files? This action cannot be undone.
            </p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                style={{
                  padding: "10px 20px",
                  background: "#fff",
                  border: "1px solid #bdc3c7",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                style={{
                  background: "#e74c3c",
                  color: "#fff",
                  border: "none",
                  padding: "10px 20px",
                }}
              >
                {isDeleting ? "Deleting..." : "Delete Note & Files"}
              </button>
            </div>
          </div>
        </div>
      )}
      {showDeleteConfirm && (
        <div
          className="modal-overlay"
          onClick={() => setShowDeleteConfirm(false)}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ padding: 24, maxWidth: 400 }}
          >
            <i
              className="fas fa-exclamation-triangle"
              style={{ fontSize: 48, color: "#f39c12" }}
            />
            <h3>Confirm Delete</h3>
            <p>
              Are you sure you want to delete this note and all associated
              files? This action cannot be undone.
            </p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                style={{
                  padding: "10px 20px",
                  background: "#fff",
                  border: "1px solid #bdc3c7",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                style={{
                  background: "#e74c3c",
                  color: "#fff",
                  border: "none",
                  padding: "10px 20px",
                }}
              >
                {isDeleting ? "Deleting..." : "Delete Note & Files"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPriorityDropdown && selectedNoteForPriority && (
        <div
          className="priority-dropdown-overlay"
          onClick={() => setShowPriorityDropdown(false)}
        >
          <div
            className="priority-dropdown"
            style={{
              position: "fixed",
              left: Math.min(
                priorityDropdownPosition.x,
                window.innerWidth - 200,
              ),
              top: Math.min(
                priorityDropdownPosition.y,
                window.innerHeight - 200,
              ),
              zIndex: 1000,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="priority-option"
              onClick={() => handlePriorityChange(1)}
            >
              <div className="priority-color-dot priority-1" />
              <span className="priority-label">No Priority</span>
            </div>
            <div
              className="priority-option"
              onClick={() => handlePriorityChange(3)}
            >
              <div className="priority-color-dot priority-3" />
              <span className="priority-label">Medium Priority</span>
            </div>
            <div
              className="priority-option"
              onClick={() => handlePriorityChange(4)}
            >
              <div className="priority-color-dot priority-4" />
              <span className="priority-label">High Priority</span>
            </div>
            <div
              className="priority-option"
              onClick={() => handlePriorityChange(5)}
            >
              <div className="priority-color-dot priority-5" />
              <span className="priority-label">Completed</span>
            </div>
          </div>
        </div>
      )}

      {showViewModal && viewNote && (
        <ViewNoteModal
          noteId={viewNote.id}
          jobId={viewNote.jobId}
          workspace={viewNote.workspace}
          job={viewNote.job}
          project={viewNote.project}
          onClose={() => {
            setShowViewModal(false);
            setViewNote(null);
          }}
          onViewAttachments={handleViewAttachments}
          currentTheme="gray"
          userid={userid}
          scrollToNoteId={viewNote.id}
        />
      )}

      {showNewModal && (
        <NewNoteModal
          ref={newNoteModalRef}
          isOpen={showNewModal}
          onClose={() => {
            setPrefilledData(null);
            setShowNewModal(false);
          }}
          refreshNotes={refreshNotes}
          refreshFilteredNotes={() => {}}
          addSiteNote={addSiteNote}
          onUploadDocument={onUploadDocument}
          onDeleteDocument={onDeleteDocument}
          defWorkSpaceId={defaultUserWorkspaceID}
          userworksaces={userWorkspace}
          prefilledData={prefilledData}
          source={modalSource}
          defaultWorkspaceRole={defaultWorkspaceRole}
          fetchNotesWithFilters={fetchNotesWithFilters}
          selectedFilters={selectedFilters}
          hasActiveFilters={hasActiveFilters}
          viewMode={viewMode}
          refreshStackedView={refreshStackedViewWithNewNote}
          hasActiveSearchText={hasActiveSearchText}
          performSearching={performSearch}
          searchTerm={searchTerm}
          fetchNotesForTask={fetchNotesForTask}
          fetchTasksForJob={fetchTasksForJob}
        />
      )}

      {showEditModal && selectedNote && (
        <EditNoteModal
          note={selectedNote}
          onClose={() => {
            setShowEditModal(false);
            refreshNotes();
          }}
          refreshNotes={refreshNotes}
          updateNote={updateNote}
          deleteDocument={onDeleteDocument}
          uploadDocument={onUploadDocument}
          projects={projects}
          jobs={jobs}
          defaultUserWorkspaceID={defaultUserWorkspaceID}
          onPriorityUpdate={handlePriorityUpdate}
          openToPriorityTab={true}
          fetchNotesWithFilters={fetchNotesWithFilters}
          selectedFilters={selectedFilters}
          hasActiveFilters={hasActiveFilters}
          viewMode={viewMode}
          refreshStackedView={refreshStackedViewWithNewNote}
          fetchNotesForTask={fetchNotesForTask}
        />
      )}

      {showAttachedFileModal && selectedFileNote && (
        <AttachedFileModal
          note={selectedFileNote}
          onClose={() => {
            setShowAttachedFileModal(false);
            setSelectedFileNote(null);
          }}
          refreshNotes={refreshNotes}
          updateNote={updateNote}
          projects={projects}
          jobs={jobs}
          uploadDocument={onUploadDocument}
          onDeleteDocument={onDeleteDocument}
        />
      )}

      {showImageViewer && selectedImage && selectedImageNote && (
        <InlineImageViewer
          image={selectedImage.image}
          note={selectedImageNote}
          currentIndex={selectedImage.index}
          totalImages={selectedImage.total}
          onClose={() => {
            setShowImageViewer(false);
            setSelectedImage(null);
            setSelectedImageNote(null);
          }}
          onNext={handleNextImage}
          onPrev={handlePrevImage}
          apiUrl={apiUrl}
        />
      )}

      {showNewTaskModal && (
        <NewTaskModal
          isOpen={showNewTaskModal}
          onClose={() => {
            setShowNewTaskModal(false);
          }}
          defaultWorkspaceRole={defaultWorkspaceRole}
          userworksaces={userWorkspace}
          refreshNotes={refreshNotes}
          isEditMode={true}
          editTaskData={editingTask}
        />
      )}

      <UrlNoteModal
        isOpen={isUrlNoteModalOpen}
        noteId={urlNoteId}
        taskId={urlTaskId}
        itemType={modalItemType}
        onClose={() => {
          setIsUrlNoteModalOpen(false);
          setUrlNoteId(null);
          setUrlTaskId(null);
          setModalItemType("note");
        }}
        apiUrl={apiUrl}
        refreshNotes={refreshNotes}
        userId={userid}
        updateNote={updateNote}
      />
    </div>
  );
};

Dashboard.propTypes = {
  notes: PropTypes.array.isRequired,
  refreshNotes: PropTypes.func.isRequired,
  addSiteNote: PropTypes.func.isRequired,
  updateNote: PropTypes.func.isRequired,
  projects: PropTypes.array,
  jobs: PropTypes.array,
  tasks: PropTypes.array,
  onUploadDocument: PropTypes.func.isRequired,
  onDeleteDocument: PropTypes.func.isRequired,
  onLogout: PropTypes.func.isRequired,
  workspaces: PropTypes.array,
  defaultUserWorkspaceID: PropTypes.any,
  defaultUserWorkspaceName: PropTypes.string,
  onUpdateDefaultWorkspace: PropTypes.func.isRequired,
  fetchProjectAndJobs: PropTypes.func.isRequired,
};

Dashboard.defaultProps = {
  projects: [],
  jobs: [],
  tasks: [],
  fetchNotes: () => {},
};

export default Dashboard;
